import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, ShieldOff, ShieldCheck, Loader2, Search, Info, RefreshCw, Route, Ban, Star } from 'lucide-react';
import { BanUserDialogs } from '@/components/BanUserDialogs';
import { UserAvatar } from '@/components/UserAvatar';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { getCorridorName } from '@/lib/corridorNames';

interface Driver {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  phone_number?: string | null;
  vehicle_plate: string | null;
  license_plate?: string | null;
  profile_picture_url: string | null;
  is_online: boolean;
  suspended_at: string | null;
  suspension_reason: string | null;
  banned_at?: string | null;
  ban_reason?: string | null;
  pending_ban_at?: string | null;
  pending_ban_reason?: string | null;
  // Legacy field fallback
  is_suspended?: boolean;
  created_at: string;
  // Live corridor — the route the driver is currently running.
  // Populated by `driver_details.current_corridor` via the
  // `list_drivers_admin` RPC. NULL when the driver hasn't picked
  // a corridor on their dispatch screen yet, even if they're
  // technically online.
  current_corridor: string | null;
  // GPS freshness (added by 20260721240000_ghost_driver_auto_offline).
  // A driver broadcasting normally updates every ~10-15s; long silence
  // with is_online=true means the app died without going offline.
  last_location_updated_at?: string | null;
  gps_staleness_seconds?: number | null;
}

function isSuspended(d: Driver): boolean {
  return d.suspended_at != null || d.is_suspended === true;
}

function isBanned(d: Driver): boolean {
  return d.banned_at != null;
}

function isBanPending(d: Driver): boolean {
  return d.banned_at == null && d.pending_ban_at != null;
}

/**
 * Truthful presence state. The raw `is_online` flag alone is
 * misleading: it stays TRUE when a driver's app dies without an
 * explicit go-offline. Cross it with GPS freshness (same 5-minute
 * "Lost" threshold the Live Map uses) so the roster and the map can
 * never disagree about who is really live.
 */
type PresenceState = 'live' | 'stale' | 'no_gps' | 'offline';

function presenceState(d: Driver): PresenceState {
  if (!d.is_online) return 'offline';
  const s = d.gps_staleness_seconds;
  if (s == null) return 'no_gps';
  return s < 300 ? 'live' : 'stale';
}

function formatGpsAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Ratings window shown on the roster. */
const RATING_WINDOW_DAYS = 90;

interface RatingSummary {
  count: number;
  average: number;
  /** 1-2 star ratings in the last 30 days. */
  recentLowCount: number;
  /** 1-3 star ratings, newest first, for the drawer. */
  lowRatings: { stars: number; comment: string | null; created_at: string }[];
  needsReview: boolean;
}

/**
 * Passenger ratings are private: drivers never see them, and nothing in
 * dispatch reads them. They exist so ops can spot a pattern and talk to the
 * driver. A driver is flagged when the average over 5+ ratings drops below
 * 3.5, or when 3 or more 1-2 star ratings land inside 30 days.
 */
function summariseRatings(
  rows: { driver_id: string | null; stars: number; comment: string | null; created_at: string }[],
): Map<string, RatingSummary> {
  const since30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const byDriver = new Map<string, RatingSummary & { total: number }>();
  for (const r of rows) {
    if (!r.driver_id) continue;
    const entry = byDriver.get(r.driver_id) ?? {
      count: 0, average: 0, total: 0, recentLowCount: 0, lowRatings: [], needsReview: false,
    };
    entry.count += 1;
    entry.total += r.stars;
    if (r.stars <= 2 && new Date(r.created_at).getTime() >= since30) entry.recentLowCount += 1;
    if (r.stars <= 3) entry.lowRatings.push({ stars: r.stars, comment: r.comment, created_at: r.created_at });
    byDriver.set(r.driver_id, entry);
  }
  const out = new Map<string, RatingSummary>();
  byDriver.forEach((e, id) => {
    const average = e.total / e.count;
    out.set(id, {
      count: e.count,
      average,
      recentLowCount: e.recentLowCount,
      lowRatings: e.lowRatings,
      needsReview: (e.count >= 5 && average < 3.5) || e.recentLowCount >= 3,
    });
  });
  return out;
}

export default function Drivers() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ratings, setRatings] = useState<Map<string, RatingSummary>>(new Map());

  // Drawer state
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Suspend dialog
  const [suspendDialog, setSuspendDialog] = useState<Driver | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reinstate dialog
  const [reinstateDialog, setReinstateDialog] = useState<Driver | null>(null);

  // Permanent ban dialogs
  const [banDialog, setBanDialog] = useState<Driver | null>(null);
  const [unbanDialog, setUnbanDialog] = useState<Driver | null>(null);

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('list_drivers_admin');
      if (error) throw error;
      const response = data as any;
      const list = response?.drivers ?? (Array.isArray(data) ? data : []);
      setDrivers(list);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
    void fetchRatings();
  };

  // Best-effort: the roster still works if ratings fail to load.
  const fetchRatings = async () => {
    const since = new Date(Date.now() - RATING_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
    const { data, error } = await supabase
      .from('ride_ratings')
      .select('driver_id, stars, comment, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(5000);
    if (error) {
      console.warn('[Drivers] ratings failed to load:', error.message);
      return;
    }
    setRatings(summariseRatings(data ?? []));
  };

  // ─── Suspend action ────────────────────────────────────────────────────────

  const openSuspendDialog = (driver: Driver) => {
    setSuspendDialog(driver);
    setSuspendReason('');
    setDrawerOpen(false);
  };

  const handleSuspend = async () => {
    if (!suspendDialog) return;
    if (suspendReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('set_user_suspension', {
        p_user_id: suspendDialog.id,
        p_suspended: true,
        p_reason: suspendReason.trim(),
      });
      if (error) throw error;

      // Optimistic update
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === suspendDialog.id
            ? { ...d, suspended_at: new Date().toISOString(), suspension_reason: suspendReason.trim(), is_suspended: true }
            : d
        )
      );
      if (selectedDriver?.id === suspendDialog.id) {
        setSelectedDriver((prev) => prev ? { ...prev, suspended_at: new Date().toISOString(), suspension_reason: suspendReason.trim(), is_suspended: true } : prev);
      }

      toast.success('Driver suspended');
      setSuspendDialog(null);
      setSuspendReason('');

      // Confirm from backend
      fetchDrivers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to suspend driver');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Reinstate action ──────────────────────────────────────────────────────

  const openReinstateDialog = (driver: Driver) => {
    setReinstateDialog(driver);
    setDrawerOpen(false);
  };

  const handleReinstate = async () => {
    if (!reinstateDialog) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.rpc('set_user_suspension', {
        p_user_id: reinstateDialog.id,
        p_suspended: false,
        p_reason: null,
      });
      if (error) throw error;

      // Optimistic update
      setDrivers((prev) =>
        prev.map((d) =>
          d.id === reinstateDialog.id
            ? { ...d, suspended_at: null, suspension_reason: null, is_suspended: false }
            : d
        )
      );
      if (selectedDriver?.id === reinstateDialog.id) {
        setSelectedDriver((prev) => prev ? { ...prev, suspended_at: null, suspension_reason: null, is_suspended: false } : prev);
      }

      toast.success('Driver reinstated');
      setReinstateDialog(null);

      // Confirm from backend
      fetchDrivers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reinstate driver');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Drawer ────────────────────────────────────────────────────────────────

  const openDrawer = (driver: Driver) => {
    setSelectedDriver(driver);
    setDrawerOpen(true);
  };

  // ─── Filter ────────────────────────────────────────────────────────────────

  const filtered = search
    ? drivers.filter((d) =>
        d.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        d.vehicle_plate?.toLowerCase().includes(search.toLowerCase()) ||
        d.phone?.includes(search)
      )
    : drivers;

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Drivers</h1>
            <p className="text-sm text-gray-400 mt-1">Driver roster and suspension controls</p>
          </div>
          <div className="flex items-center gap-2">
            {/*
              Manual refresh. The page doesn't subscribe to realtime,
              so the Online / Corridor columns only refresh on
              (re)fetch. Cheaper and more transparent than a polling
              interval — operators click when they expect data to
              have changed (e.g. just after a driver flipped online).
            */}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchDrivers}
              disabled={loading}
              className="text-gray-400 hover:text-white"
              title="Refresh driver roster"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Badge variant="outline" className="text-gray-400 border-gray-700">
              {drivers.length} total
            </Badge>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by name, plate, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-500"
          />
        </div>

        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">{search ? 'No drivers match your search' : 'No drivers found'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Name</TableHead>
                    <TableHead className="text-gray-400">Contact</TableHead>
                    <TableHead className="text-gray-400">Online</TableHead>
                    <TableHead className="text-gray-400">Corridor</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Rating</TableHead>
                    <TableHead className="text-gray-400">Joined</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((d) => (
                    <TableRow
                      key={d.id}
                      className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => openDrawer(d)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <UserAvatar
                            size="md"
                            name={d.full_name}
                            src={d.profile_picture_url}
                            ring={isSuspended(d) ? 'red' : presenceState(d) === 'live' ? 'green' : 'none'}
                          />
                          <span className="text-white font-medium">{d.full_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <ContactRow label="Phone" value={formatDominicaPhone(d.phone_number ?? d.phone)} callable />
                          <ContactRow label="Plate" value={d.license_plate ?? d.vehicle_plate} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {/*
                          Presence, not just the flag. Four states:
                            • Live — flag on AND GPS in the last 5 min
                              (matches the Live Map's threshold, so the
                              two pages always agree).
                            • Online · GPS Xm — flag on but GPS silent:
                              the app almost certainly died without
                              going offline. The server sweep flips
                              these to Offline after 45 min of silence.
                            • Online · no GPS — flag on, never sent a
                              fix this session.
                            • Offline — flag off.
                        */}
                        {(() => {
                          const state = presenceState(d);
                          if (state === 'live') {
                            return (
                              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5 inline-block" />
                                Live
                              </Badge>
                            );
                          }
                          if (state === 'stale' || state === 'no_gps') {
                            const age = d.gps_staleness_seconds != null ? formatGpsAge(d.gps_staleness_seconds) : null;
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-help">
                                    {age ? `Online · GPS ${age} ago` : 'Online · no GPS'}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                                  <p className="text-xs">
                                    The online flag is set but this driver hasn't sent a GPS fix
                                    {age ? ` in ${age}` : ' yet'}. Their app likely closed without
                                    going offline. Auto-marked Offline after 45 min of silence.
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            );
                          }
                          return (
                            <Badge className="bg-gray-700/50 text-gray-400 border-gray-600">
                              Offline
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {/*
                          Live corridor — driven by `driver_details.
                          current_corridor`, the same column the
                          dispatch screen writes to when the driver
                          picks "Where are you running today?". Three
                          states:
                            • Offline driver → "—" (they aren't
                              running anything; showing a stale name
                              would be misleading).
                            • Online + corridor set → corridor name.
                            • Online + no corridor → "Not assigned"
                              in amber (a rare anomaly worth flagging
                              — likely a driver mid-onboarding or a
                              client bug that allowed online without
                              corridor selection).
                        */}
                        {!d.is_online ? (
                          <span className="text-gray-500 text-sm">–</span>
                        ) : d.current_corridor ? (
                          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/20 font-normal">
                            <Route className="w-3 h-3 mr-1.5 inline" />
                            {getCorridorName(d.current_corridor)}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 font-normal">
                            Not assigned
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {isBanned(d) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-red-500/10 text-red-400 border-red-500/20 cursor-help">
                                Banned
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                {d.banned_at && `Banned ${timeAgo(d.banned_at)}`}
                                {d.ban_reason && `: ${d.ban_reason}`}
                                {' · Identities blocklisted (no new accounts)'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : isBanPending(d) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 cursor-help">
                                Ban pending
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                Ban scheduled {d.pending_ban_at && timeAgo(d.pending_ban_at)}. Waiting for their
                                active trip to end, then it fires automatically.
                                {d.pending_ban_reason && ` Reason: ${d.pending_ban_reason}`}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : isSuspended(d) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-help">
                                Suspended
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                              <p className="text-xs">
                                {d.suspended_at && `Suspended ${timeAgo(d.suspended_at)}`}
                                {d.suspension_reason && `: ${d.suspension_reason}`}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const r = ratings.get(d.id);
                          if (!r) return <span className="text-gray-500 text-sm">–</span>;
                          const label = `${r.average.toFixed(1)} · ${r.count}`;
                          if (!r.needsReview) {
                            return (
                              <span className="text-gray-300 text-sm inline-flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                {label}
                              </span>
                            );
                          }
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 cursor-help">
                                  <Star className="w-3 h-3 mr-1 fill-amber-400" />
                                  {label} · Needs review
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                                <p className="text-xs">
                                  {r.recentLowCount >= 3
                                    ? `${r.recentLowCount} ratings of 1 or 2 stars in the last 30 days.`
                                    : `Average below 3.5 over ${r.count} ratings.`}{' '}
                                  Open the driver to read what passengers said.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {new Date(d.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {isBanned(d) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => { setUnbanDialog(d); setDrawerOpen(false); }}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Lift ban
                          </Button>
                        ) : isBanPending(d) ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            onClick={() => { setUnbanDialog(d); setDrawerOpen(false); }}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Cancel ban
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {isSuspended(d) ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                onClick={() => openReinstateDialog(d)}
                              >
                                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Reinstate
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                                onClick={() => openSuspendDialog(d)}
                              >
                                <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suspend
                              </Button>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => { setBanDialog(d); setDrawerOpen(false); }}
                                >
                                  <Ban className="w-3.5 h-3.5 mr-1" /> Ban
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-gray-900 border-gray-700 text-white max-w-xs">
                                <p className="text-xs">Permanent ban. Blocks new accounts on the same phone, email, license, or plate. Reversible.</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Detail Drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="right" className="bg-[#111827] border-gray-800 text-white w-[400px] sm:max-w-[400px]">
            <SheetHeader>
              <SheetTitle className="text-white">Driver Details</SheetTitle>
            </SheetHeader>

            {selectedDriver && (
              <div className="mt-6 space-y-6">
                <div className="flex items-center gap-4">
                  <UserAvatar
                    size="xl"
                    name={selectedDriver.full_name}
                    src={selectedDriver.profile_picture_url}
                    ring={isSuspended(selectedDriver) ? 'red' : presenceState(selectedDriver) === 'live' ? 'green' : 'none'}
                  />
                  <div>
                  <p className="text-lg font-semibold text-white">{selectedDriver.full_name}</p>
                  </div>
                </div>

                {/* Contact info */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Contact</p>
                  <ContactRow label="Phone" value={formatDominicaPhone(selectedDriver.phone_number ?? selectedDriver.phone)} callable />
                  <ContactRow label="Plate" value={selectedDriver.license_plate ?? selectedDriver.vehicle_plate} />
                </div>

                {/* Presence card — truthful live/stale/offline state */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Presence</p>
                  {(() => {
                    const state = presenceState(selectedDriver);
                    const age = selectedDriver.gps_staleness_seconds != null
                      ? formatGpsAge(selectedDriver.gps_staleness_seconds)
                      : null;
                    if (state === 'live') {
                      return (
                        <div>
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-sm px-3 py-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5 inline-block" />
                            Live now
                          </Badge>
                          {age && <p className="text-xs text-gray-500 mt-2">Last GPS {age} ago</p>}
                        </div>
                      );
                    }
                    if (state === 'stale' || state === 'no_gps') {
                      return (
                        <div>
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-sm px-3 py-1">
                            {age ? `Online flag set · GPS ${age} ago` : 'Online flag set · no GPS'}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-2">
                            App likely closed without going offline. Auto-marked Offline after 45 min of GPS silence.
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div>
                        <Badge className="bg-gray-700/50 text-gray-400 border-gray-600 text-sm px-3 py-1">
                          Offline
                        </Badge>
                        {age && <p className="text-xs text-gray-500 mt-2">Last GPS {age} ago</p>}
                      </div>
                    );
                  })()}
                </div>

                {/* Ratings card — private passenger ratings, ops only */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                    Passenger ratings · last {RATING_WINDOW_DAYS} days
                  </p>
                  {(() => {
                    const r = ratings.get(selectedDriver.id);
                    if (!r) return <p className="text-sm text-gray-500">No ratings yet.</p>;
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          <span className="text-white font-semibold">{r.average.toFixed(1)}</span>
                          <span className="text-sm text-gray-400">
                            from {r.count} rating{r.count === 1 ? '' : 's'}
                          </span>
                          {r.needsReview && (
                            <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                              Needs review
                            </Badge>
                          )}
                        </div>
                        {r.lowRatings.length > 0 ? (
                          <div className="space-y-2 max-h-56 overflow-y-auto">
                            {r.lowRatings.slice(0, 10).map((low, i) => (
                              <div key={i} className="border-t border-gray-800 pt-2">
                                <p className="text-xs text-amber-400">
                                  {low.stars} star{low.stars === 1 ? '' : 's'} · {timeAgo(low.created_at)}
                                </p>
                                <p className="text-xs text-gray-300 mt-0.5">
                                  {low.comment ? `"${low.comment}"` : 'No comment left.'}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">No ratings of 3 stars or lower.</p>
                        )}
                        <p className="text-xs text-gray-500">
                          Drivers never see these. Nothing is automatic: if a pattern shows up, call the
                          driver first, and check Incidents for any formal reports.
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Status card */}
                <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Status</p>
                  {isBanned(selectedDriver) ? (
                    <div>
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-sm px-3 py-1">
                        Permanently Banned
                      </Badge>
                      {selectedDriver.banned_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Since {timeAgo(selectedDriver.banned_at)} · Identities blocklisted
                        </p>
                      )}
                      {selectedDriver.ban_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedDriver.ban_reason}"
                        </p>
                      )}
                    </div>
                  ) : isBanPending(selectedDriver) ? (
                    <div>
                      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-sm px-3 py-1">
                        Ban Pending
                      </Badge>
                      <p className="text-xs text-gray-500 mt-2">
                        Fires automatically the moment their active trip ends.
                      </p>
                      {selectedDriver.pending_ban_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedDriver.pending_ban_reason}"
                        </p>
                      )}
                    </div>
                  ) : isSuspended(selectedDriver) ? (
                    <div>
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-sm px-3 py-1">
                        Suspended
                      </Badge>
                      {selectedDriver.suspended_at && (
                        <p className="text-xs text-gray-500 mt-2">
                          Since {timeAgo(selectedDriver.suspended_at)}
                        </p>
                      )}
                      {selectedDriver.suspension_reason && (
                        <p className="text-xs text-gray-400 mt-1 italic">
                          "{selectedDriver.suspension_reason}"
                        </p>
                      )}
                    </div>
                  ) : (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-sm px-3 py-1">
                      Active
                    </Badge>
                  )}
                </div>

                {/* Action buttons */}
                <div className="pt-2 space-y-2">
                  {isBanned(selectedDriver) ? (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                      onClick={() => { setUnbanDialog(selectedDriver); setDrawerOpen(false); }}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" /> Lift Ban
                    </Button>
                  ) : isBanPending(selectedDriver) ? (
                    <Button
                      className="w-full bg-orange-600 hover:bg-orange-500 text-white"
                      onClick={() => { setUnbanDialog(selectedDriver); setDrawerOpen(false); }}
                    >
                      <ShieldCheck className="w-4 h-4 mr-2" /> Cancel Scheduled Ban
                    </Button>
                  ) : (
                    <>
                      {isSuspended(selectedDriver) ? (
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                          onClick={() => openReinstateDialog(selectedDriver)}
                        >
                          <ShieldCheck className="w-4 h-4 mr-2" /> Reinstate Driver
                        </Button>
                      ) : (
                        <Button
                          className="w-full bg-amber-600 hover:bg-amber-500 text-white"
                          onClick={() => openSuspendDialog(selectedDriver)}
                        >
                          <ShieldOff className="w-4 h-4 mr-2" /> Suspend Driver
                        </Button>
                      )}
                      <Button
                        className="w-full bg-red-700 hover:bg-red-600 text-white"
                        onClick={() => { setBanDialog(selectedDriver); setDrawerOpen(false); }}
                      >
                        <Ban className="w-4 h-4 mr-2" /> Ban Permanently
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Suspend Dialog */}
        <Dialog open={!!suspendDialog} onOpenChange={(open) => { if (!open) setSuspendDialog(null); }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Suspend Driver</DialogTitle>
              <DialogDescription className="text-gray-400">
                {suspendDialog?.full_name} will not be able to accept rides until reinstated.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-gray-300">
                  Reason <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  placeholder="e.g. Abusive language, policy violation, safety concern"
                />
                {suspendReason.length > 0 && suspendReason.trim().length < 3 && (
                  <p className="text-xs text-red-400">Reason must be at least 3 characters</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSuspendDialog(null)} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={handleSuspend}
                disabled={submitting || suspendReason.trim().length < 3}
                className="bg-red-600 hover:bg-red-500 text-white"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Suspend
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reinstate Dialog */}
        <Dialog open={!!reinstateDialog} onOpenChange={(open) => { if (!open) setReinstateDialog(null); }}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Reinstate Driver</DialogTitle>
              <DialogDescription className="text-gray-400">
                Reinstate {reinstateDialog?.full_name}? They will be able to accept rides again.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-xs text-blue-300">
                  The driver will need to go online from their phone.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setReinstateDialog(null)} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={handleReinstate}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Reinstate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Permanent ban / lift ban */}
        <BanUserDialogs
          roleLabel="driver"
          banTarget={banDialog}
          unbanTarget={unbanDialog}
          onCloseBan={() => setBanDialog(null)}
          onCloseUnban={() => setUnbanDialog(null)}
          onDone={fetchDrivers}
        />
      </div>
    </TooltipProvider>
  );
}
